import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Flex, Heading, Text, Button, Badge } from '@radix-ui/themes';
import { ArrowLeftIcon, DashboardIcon, PersonIcon, CalendarIcon, EyeOpenIcon, ActivityLogIcon, MixIcon, CheckCircledIcon, UpdateIcon, UploadIcon, BarChartIcon, SpeakerLoudIcon } from '@radix-ui/react-icons';
import OrganizationSelector from '../OrganizationSelector';

interface AdminLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ title, subtitle, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { label: 'Clinics', path: '/admin/clinics', icon: <DashboardIcon /> },
    { label: 'Users', path: '/admin/users', icon: <PersonIcon /> },
    { label: 'View As', path: '/admin/view-as', icon: <EyeOpenIcon /> },
    { label: 'Scheduler', path: '/admin/scheduler', icon: <CalendarIcon /> },
    { label: 'Tellihealth', path: '/admin/tellihealth', icon: <ActivityLogIcon /> },
    { label: 'Devices', path: '/admin/devices', icon: <MixIcon /> },
    { label: 'Eligibility', path: '/admin/eligibility', icon: <CheckCircledIcon /> },
    { label: 'EHR Sync', path: '/admin/ehr-sync', icon: <UpdateIcon /> },
    { label: 'EHR Uploads', path: '/admin/ehr-uploads', icon: <UploadIcon /> },
    { label: 'Voice Calls', path: '/admin/voice-calls', icon: <SpeakerLoudIcon /> },
    { label: 'Adoption', path: '/admin/adoption-tracking', icon: <BarChartIcon /> },
  ];

  return (
    <Box className="min-h-screen bg-slate-1">
      <Box className="border-b border-slate-6 bg-white sticky top-0 z-50">
        <Container size="4">
          <Flex align="center" justify="between" py="3" wrap="wrap" gap="3">
            <Flex align="center" gap="3">
              <Button variant="ghost" onClick={() => navigate('/')}
                aria-label="Back to dashboard"
              >
                <ArrowLeftIcon />
              </Button>
              <Heading size="6" className="text-slate-800">Admin Console</Heading>
              <Badge color="blue">Internal</Badge>
            </Flex>
            <Flex align="center" gap="2" wrap="wrap">
              <OrganizationSelector />
              {navItems.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? 'solid' : 'soft'}
                    onClick={() => navigate(item.path)}
                  >
                    {item.icon}
                    {item.label}
                  </Button>
                );
              })}
            </Flex>
          </Flex>
        </Container>
      </Box>

      <Container size="4" className="py-6">
        <Flex direction="column" gap="1" className="mb-5">
          <Heading size="5" className="text-slate-800">{title}</Heading>
          {subtitle && (
            <Text size="2" color="gray">
              {subtitle}
            </Text>
          )}
        </Flex>
        {children}
      </Container>
    </Box>
  );
};

export default AdminLayout;
